import React from "react";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { closeSnackbarAction } from "@/redux/slices/uiComponents";
import { CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";

type Severity = "success" | "error" | "warning" | "info";

const severityClasses: Record<Severity, string> = {
  success: "bg-[#02844C] text-white",
  error: "bg-red-500 text-white",
  warning: "bg-yellow-400 text-black",
  info: "bg-blue-500 text-white",
};

const severityIcons: Record<Severity, React.ReactNode> = {
  success: <CheckCircle className="w-5 h-5" />,
  error: <XCircle className="w-5 h-5" />,
  warning: <AlertTriangle className="w-5 h-5" />,
  info: <Info className="w-5 h-5" />,
};


const AppSnackbar = () => {
  const dispatch = useAppDispatch();
  const { alertopen, alertseverity, alertmessage, alertduration } =
    useAppSelector((state) => state.uiComponents);

  const severity: Severity =
    (["success", "error", "warning", "info"].includes(alertseverity)
      ? (alertseverity as Severity)
      : "info");

  const handleClose = () => {
    dispatch(closeSnackbarAction());
  };

  React.useEffect(() => {
    if (alertopen && alertduration) {
      const timer = setTimeout(() => {
        handleClose();
      }, alertduration);
      return () => clearTimeout(timer);
    }
  }, [alertopen, alertduration]);

  if (!alertopen) return null;

  return (
    <div
      className={`
        fixed top-[75px] right-3 z-50 flex items-center gap-2 
        px-4 py-3 rounded-lg shadow-lg transition-all duration-300 
        ${severityClasses[severity]}
      `}
    >
      {severityIcons[severity]}
      <span
        className="font-poppins font-normal text-base"
        dangerouslySetInnerHTML={{ __html: alertmessage }}
      />
      <button
        onClick={handleClose}
        className="ml-3 text-white hover:text-gray-200 focus:outline-none"
      >
        ✕
      </button>
    </div>
  );
};

export default AppSnackbar;
